import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Users, Key, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useParams, Link } from "react-router-dom";
import { z } from "zod";

const emailSchema = z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters");
const fullNameSchema = z.string().trim().min(1, "Full name is required").max(100, "Full name must be less than 100 characters");
const phraseSchema = z.string().trim().min(1, "3-word phrase is required").regex(/^[a-z]+-[a-z]+-[a-z]+$/i, "Phrase must be in format: word-word-word");

export default function ParentPortal() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { ptaId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Sign in state
  const [signinEmail, setSigninEmail] = useState("");
  const [signinPassword, setSigninPassword] = useState("");
  
  // Register state
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPhrase, setRegisterPhrase] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setCheckingAuth(false);
      
      if (session?.user) {
        // Check if they're already a parent and redirect
        checkParentStatus(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        setTimeout(() => {
          checkParentStatus(session.user.id);
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkParentStatus = async (userId: string) => {
    const { data: parentData } = await supabase
      .from('parents')
      .select('pta_id')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (parentData?.pta_id) {
      // Already registered as a parent, redirect to fundraising page
      const redirectPtaId = ptaId || parentData.pta_id;
      navigate(`/fundraising/${redirectPtaId}`);
    } else {
      // No parent record - sign out so they can register properly
      await supabase.auth.signOut();
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    try {
      emailSchema.parse(signinEmail);
      passwordSchema.parse(signinPassword);
    } catch (error: any) {
      if (error.errors) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err: any) => {
          newErrors[err.path?.[0] || 'general'] = err.message;
        });
        setErrors(newErrors);
        setIsLoading(false);
        return;
      }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: signinEmail,
      password: signinPassword,
    });

    if (error) {
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // Check if email is verified in profiles table
    const { data: profile } = await supabase
      .from('profiles')
      .select('email_verified')
      .eq('id', data.user.id)
      .single();

    if (!profile?.email_verified) {
      await supabase.auth.signOut();
      toast({
        title: "Email not verified",
        description: "Please check your email and click the verification link before signing in.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    toast({
      title: "Signed in successfully",
      description: "Welcome back!",
    });
    
    setIsLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate inputs first (synchronous)
    try {
      fullNameSchema.parse(registerName);
      emailSchema.parse(registerEmail);
      passwordSchema.parse(registerPassword);
      phraseSchema.parse(registerPhrase);
    } catch (error: any) {
      if (error.errors) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err: any) => {
          const field = err.path?.[0] || 'general';
          newErrors[field] = err.message;
        });
        setErrors(newErrors);
        return;
      }
    }

    setIsLoading(true);

    // First, validate the 3-word phrase and get the PTA ID
    const { data: ptaIdData, error: phraseError } = await supabase
      .rpc('get_pta_by_three_word_phrase', { phrase: registerPhrase.toLowerCase().trim() });

    if (phraseError || !ptaIdData) {
      toast({
        title: "Invalid phrase",
        description: "The 3-word phrase you entered doesn't match any school. Please check with your school and try again.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // Get PTA name for the email
    const { data: ptaInfo } = await supabase
      .from('ptas')
      .select('name')
      .eq('id', ptaIdData)
      .single();

    // Sign up the user with auto-confirm enabled (email verification handled separately)
    const { data: authData, error: signupError } = await supabase.auth.signUp({
      email: registerEmail,
      password: registerPassword,
      options: {
        data: {
          full_name: registerName,
          pta_id: ptaIdData,
        }
      }
    });

    if (signupError) {
      toast({
        title: "Registration failed",
        description: signupError.message,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    if (!authData.user) {
      toast({
        title: "Registration failed",
        description: "Could not create user account",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // IMMEDIATELY sign out and redirect to prevent any flash of authenticated content
    await supabase.auth.signOut();
    navigate(`/check-parent-email?email=${encodeURIComponent(registerEmail)}`);

    // Send verification email in the background (user already redirected)
    supabase.functions.invoke('send-parent-verification', {
      body: {
        userId: authData.user.id,
        email: registerEmail,
        fullName: registerName,
        ptaId: ptaIdData,
        ptaName: ptaInfo?.name || 'Your PTA',
      }
    }).catch((verifyError) => {
      console.error('Error sending verification email:', verifyError);
    });
  };

  const handlePasswordReset = async () => {
    if (!signinEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address first",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await supabase.functions.invoke("send-password-reset", {
        body: { email: signinEmail },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast({
        title: "Password reset email sent",
        description: "If an account exists, you'll receive a password reset link shortly",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
      });
    }
  };

  if (checkingAuth) {
    return (
      <main className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <p className="text-muted-foreground">Loading...</p>
      </main>
    );
  }


  return (
    <main className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-lg bg-gradient-primary flex items-center justify-center">
              <Users className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground">Parent Portal</h1>
          <p className="text-muted-foreground mt-2">Access your school's PTA fundraising</p>
        </div>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Welcome</CardTitle>
            <CardDescription>Sign in or create your parent account</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="register">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input 
                      id="signin-email" 
                      type="email" 
                      placeholder="your@email.com"
                      value={signinEmail}
                      onChange={(e) => setSigninEmail(e.target.value)}
                      required
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input 
                      id="signin-password" 
                      type="password" 
                      placeholder="Enter your password"
                      value={signinPassword}
                      onChange={(e) => setSigninPassword(e.target.value)}
                      required
                    />
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="link" 
                    className="w-full text-sm"
                    onClick={handlePasswordReset}
                  >
                    Forgot password?
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input 
                      id="register-name" 
                      placeholder="John Smith"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      required
                    />
                    {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input 
                      id="register-email" 
                      type="email" 
                      placeholder="your@email.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input 
                      id="register-password" 
                      type="password" 
                      placeholder="Create a password (min 6 characters)"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                    />
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-phrase" className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-primary" />
                      3-Word Phrase (from your school)
                    </Label>
                    <Input 
                      id="register-phrase" 
                      placeholder="happy-sunny-day"
                      value={registerPhrase}
                      onChange={(e) => setRegisterPhrase(e.target.value)}
                      required
                    />
                    {errors.phrase && <p className="text-sm text-destructive">{errors.phrase}</p>}
                    <p className="text-xs text-muted-foreground">
                      This phrase links you to the correct PTA. Get it from your school.
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="mt-4 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Parents can access multiple PTAs with one account
          </p>
          <Link to="/" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </main>
  );
}